"use client";

import { Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function UnleashYourPotential() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1244" id="unleash-your-potential" className="relative bg-background text-foreground overflow-hidden py-24 md:py-44">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1280px] px-6 md:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {}
          <div ref={textRef} className={`lg:col-span-7 transition-all duration-700 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="font-default uppercase mb-8 flex items-center gap-3" style={{
            fontSize: "0.72rem",
            letterSpacing: "0.3em",
            color: "var(--primary)",
            fontWeight: 500
          }}>
              <span className="inline-block w-8 h-px bg-primary" aria-hidden="true" />
              YT.№ 001 — YOUTH TRIBE
            </div>

            {}
            <h1 className="font-default text-foreground font-black mb-10" style={{
            fontSize: "clamp(2.75rem, 6.4vw, 5.75rem)",
            letterSpacing: "-0.04em",
            lineHeight: 0.9,
            fontWeight: 900
          }}>
              Building a{" "}
              <span className="relative inline-block" style={{
              color: "var(--primary)"
            }}>
                dynamic
                <span className={`absolute left-0 -bottom-1 h-px bg-primary origin-left transition-transform duration-700 ease-out ${textVisible ? "scale-x-100" : "scale-x-0"}`} style={{
                width: "100%",
                transitionDelay: "400ms"
              }} aria-hidden="true" />
              </span>{" "}
              community for the next generation
            </h1>

            {}
            <p className="text-foreground/85 max-w-[52ch] mb-12" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(1.0625rem, 1.2vw, 1.1875rem)",
            lineHeight: 1.65,
            letterSpacing: "0em"
          }}>
              <span className="float-left font-default mr-3 mt-2 leading-[0.8]" style={{
              color: "var(--primary)",
              fontSize: "4.5rem",
              fontWeight: 800,
              lineHeight: 0.8
            }}>
                W
              </span>
              e are a high-energy youth group dedicated to connection, growth, and shared experiences. From summer camp adventures and team sports to live music nights, Youth Tribe provides a space where every young person can find their rhythm and belong to something bigger.
            </p>

            {}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <Button asChild className="bg-primary text-white hover:bg-primary/90 px-7 py-6 h-auto font-default transition-colors duration-220" style={{
              fontSize: "0.95rem",
              fontWeight: 600,
              letterSpacing: "0.05em"
            }}>
                <Link to="/ParentHubPage">
                  Download <span className="ml-1">Forms</span>
                </Link>
              </Button>

              <Link to="/OurRhythmPage" className="group relative inline-flex items-center text-foreground font-default self-start sm:self-auto" style={{
              fontSize: "1rem"
            }}>
                Explore our activities
                <span className="absolute left-0 -bottom-1 h-px bg-primary transition-all duration-200 ease-in-out group-hover:h-0.5" style={{
                width: "100%"
              }} aria-hidden="true" />
                <span className="ml-2 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true">→</span>
              </Link>
            </div>
          </div>

          {}
          <div ref={imgRef} className={`lg:col-span-5 relative transition-all duration-700 ease-out ${imgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: "150ms"
        }}>
            {}
            <Leaf className="absolute -top-4 -right-2 w-16 h-16 -rotate-45 z-10" strokeWidth={1.25} style={{
            color: "var(--primary)",
            opacity: 0.7
          }} aria-hidden="true" />

            <div className="relative">
              {}
              <div className="relative aspect-[4/5] overflow-hidden border border-border" style={{
              backgroundColor: "var(--card)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/youth_tribe_summer_camp_action.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/youth_tribe_summer_camp_action.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701369/img/youth_tribe_summer_camp_action.png-wvc-sizes" alt="Youth Tribe members participating in high-energy summer camp activities" className="w-full h-full object-cover" loading="lazy" decoding="async" />

                {}
                <div className="absolute inset-3 border pointer-events-none" style={{
                borderColor: "hsl(226 91% 39% / 0.15)"
              }} aria-hidden="true" />
              </div>

              {}
              <p className="mt-5 text-muted-foreground max-w-[28ch] ml-2" style={{
              fontFamily: "var(--font-sans)",
              fontSize: "0.875rem",
              lineHeight: 1.45,
              letterSpacing: "0.05em"
            }}>
                — Capturing the energy of our latest summer camp highlights.
              </p>

              {}
              <div className="absolute top-4 left-4 font-default uppercase bg-background/85 px-2 py-1" style={{
              fontSize: "0.65rem",
              letterSpacing: "0.3em",
              color: "var(--foreground)",
              fontWeight: 600
            }}>
                Plate I — Tribe
              </div>

              {}
              <div className="absolute bottom-0 left-8 w-[3px] bg-primary" style={{
              height: "32px",
              transform: "translateY(100%)"
            }} aria-hidden="true">
                <div className="absolute -bottom-2 left-0 w-0 h-0" style={{
                borderLeft: "1.5px solid transparent",
                borderRight: "1.5px solid transparent",
                borderTop: "8px solid var(--primary)"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative mt-24 md:mt-32 h-6 w-full overflow-hidden" style={{
      background: "repeating-linear-gradient(110deg, hsl(226 91% 39%) 0px, hsl(226 91% 39%) 12px, hsl(190 100% 50%) 24px, hsl(226 60% 8%) 36px, hsl(226 91% 39%) 52px)"
    }} aria-hidden="true">
        <div className="absolute inset-0 opacity-40 mix-blend-overlay" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 50' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='m'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.02 0.6' numOctaves='2' /%3E%3CfeDisplacementMap in='SourceGraphic' scale='20' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23m)' fill='%23000' /%3E%3C/svg%3E\")"
      }} />
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-primary/60" aria-hidden="true" />
    </section>;
}